import { User } from 'lucide-react'

import { CopyQuotesLinkButton } from '@/components/quotes/CopyQuotesLinkButton'
import { CopyButton } from '@/components/ui/CopyButton'
import { NavBar } from '@/routes/Public/marketing/components/NavBar'
import { ProductFlowNav } from '@/routes/Public/marketing/components/ProductFlowNav'
import { AccountMenuNavDemo } from '../AccountMenuNavDemo'
import { AccountNavDemo } from '../AccountNavDemo'
import { DEMO_APPLICATION_ID, MATCHED_BROKER, NAV_LINKS } from '../navFixtures'
import { Demo, DsSection, PhonePreview } from '../primitives'

const ACCOUNT_VARIANT = '<ProductFlowNav account={{ org, tabs, profile }} />'

const MOBILE_VARIANTS: { variant: string; value: string }[] = [
  { variant: 'matched-broker', value: '<ProductFlowNav matchedBroker={broker} allowBrokerNote />' },
  {
    variant: 'matched-broker-cart',
    value: '<ProductFlowNav matchedBroker={broker} allowBrokerNote cartCount={2} />',
  },
  { variant: 'no-broker-cart', value: '<ProductFlowNav hideLogin cartCount={2} />' },
  { variant: 'site', value: '<NavBar navLinks={links} ctaLabel="…" />' },
  { variant: 'account', value: ACCOUNT_VARIANT },
]

// Tall enough that the marketing nav's drawer is fully visible once opened.
const MOBILE_FRAME_HEIGHT = 420

function MobileDemo({ variant, value }: { variant: string; value: string }) {
  return (
    <div className="flex flex-col items-start gap-2">
      <PhonePreview
        src={`/design-system/frames/nav?variant=${variant}`}
        title={`${variant} nav at 375px`}
        height={MOBILE_FRAME_HEIGHT}
      />
      <div className="text-ink-400 flex items-center gap-1 font-mono text-[10px]">
        <span>{value}</span>
        <CopyButton
          value={value}
          className="text-ink-400 hover:text-ink size-4 bg-transparent hover:bg-transparent"
        />
      </div>
    </div>
  )
}

export function NavigationSection() {
  return (
    <DsSection id="navigation" title="Navigation" marketing>
      <Demo label="Light" className="border-ink-100 overflow-hidden rounded-sm border">
        <NavBar
          theme="light"
          navLinks={NAV_LINKS}
          phoneNumber="(770) 765-0331"
          utilityLinks={[{ label: 'Login', icon: <User />, href: '#navigation' }]}
          ctaLabel="Get your quotes"
          ctaHref="#navigation"
          disableScrollHide
        />
      </Demo>
      <Demo label="Dark" className="overflow-hidden rounded-sm">
        <NavBar
          theme="dark"
          navLinks={NAV_LINKS}
          phoneNumber="(770) 765-0331"
          utilityLinks={[{ label: 'Login', icon: <User />, href: '#navigation' }]}
          ctaLabel="Get your quotes"
          ctaHref="#navigation"
          disableScrollHide
        />
      </Demo>
      <Demo
        label="Product flow — stripped-back nav for the DOT funnel/estimate results"
        className="border-ink-100 overflow-hidden rounded-sm border"
      >
        <ProductFlowNav disableScrollHide />
      </Demo>
      <Demo
        label="Product flow, hideLogin — for routes only reachable while already signed in (e.g. /application)"
        className="border-ink-100 overflow-hidden rounded-sm border"
      >
        <ProductFlowNav disableScrollHide hideLogin />
      </Demo>
      <Demo
        label="Product flow, matched broker — for an already-matched customer (e.g. Quotes page)"
        className="border-ink-100 overflow-hidden rounded-sm border"
      >
        <ProductFlowNav
          disableScrollHide
          matchedBroker={MATCHED_BROKER}
          allowBrokerNote
          rightAction={<CopyQuotesLinkButton applicationId={DEMO_APPLICATION_ID} />}
        />
      </Demo>
      <Demo
        label="Product flow, matched broker with cart"
        className="border-ink-100 overflow-hidden rounded-sm border"
      >
        <ProductFlowNav
          disableScrollHide
          matchedBroker={MATCHED_BROKER}
          allowBrokerNote
          cartCount={2}
          rightAction={<CopyQuotesLinkButton applicationId={DEMO_APPLICATION_ID} />}
        />
      </Demo>
      <Demo
        label="Product flow, no broker with cart — the Quotes page for a customer with no sales owner"
        className="border-ink-100 overflow-hidden rounded-sm border"
      >
        <ProductFlowNav
          disableScrollHide
          hideLogin
          cartCount={2}
          rightAction={<CopyQuotesLinkButton applicationId={DEMO_APPLICATION_ID} />}
        />
      </Demo>
      <Demo
        label="Product flow, account menu — signed-in flows (e.g. /application, /quotes)"
        className="border-ink-100 overflow-hidden rounded-sm border"
      >
        <AccountMenuNavDemo />
      </Demo>
      <Demo
        label="Product flow, account — signed in, in their account (the app shell)"
        className="border-ink-100 overflow-hidden rounded-sm border"
      >
        <AccountNavDemo />
        <div className="text-ink-400 flex items-center gap-1 px-2 pt-2 pb-1 font-mono text-[10px]">
          <span>{ACCOUNT_VARIANT}</span>
          <CopyButton
            value={ACCOUNT_VARIANT}
            className="text-ink-400 hover:text-ink size-4 bg-transparent hover:bg-transparent"
          />
        </div>
      </Demo>
      {/* Real 375px viewports — the mobile rules are viewport media queries, so a narrow div can't show them. */}
      <Demo label="Mobile — 375px" className="flex flex-wrap items-start gap-6">
        {MOBILE_VARIANTS.map((v) => (
          <MobileDemo key={v.variant} {...v} />
        ))}
      </Demo>
    </DsSection>
  )
}
